import { Button, ConfirmDialog, TOUCH } from "@nymbl/nos2-rules";
// src/components/time/RevokeSignOff.tsx
import { Undo2 } from "lucide-react";
import { useState } from "react";
import { useRevokeSignOff, useRevokeWeekAndMonth } from "@/hooks/use-time-signoffs";
import { formatMonth, formatWeekRange } from "@/lib/time/dates";
import type { SignOffRow } from "@/schemas/time";

/** Vinyl's confirm text (TE-46), worded for this app. */
const REVOKE_WARNING = "Revoking your sign-off can result in a Late entry upon resubmission.";

export function periodLabel(row: Pick<SignOffRow, "periodType" | "start">): string {
  return row.periodType === "week" ? formatWeekRange(row.start) : formatMonth(row.start);
}

/**
 * The confirm's text and action. A week whose month is signed off is revoked with its month
 * (one action); if the month went through and the week did not, it says so and retries the week.
 */
export function revokeConfirm(state: { pairsMonth: boolean; monthRevoked: boolean }): {
  description: string;
  confirmLabel: string;
} {
  if (state.monthRevoked) {
    return {
      description: "Month revoked. The week is still signed off.",
      confirmLabel: "Retry week",
    };
  }
  return {
    description: REVOKE_WARNING,
    confirmLabel: state.pairsMonth ? "Revoke week and month" : "Revoke",
  };
}

interface RevokeSignOffProps {
  row: SignOffRow;
  /** The week's month when it is signed off and revocable (`signedOffMonthOf`), else null. */
  month: SignOffRow | null;
  /** "row": a Revoke button in the Sign-off dialog; "header": the Time page header action. */
  look: "row" | "header";
}

/**
 * Revoke for one signed-off week or month (TE-46/47): a button named for its period, then a
 * confirm with the late warning. The caller only renders it for rows `revokeBlock` allows.
 */
export function RevokeSignOff({ row, month, look }: RevokeSignOffProps) {
  const [open, setOpen] = useState(false);
  // The month half landed and the week half did not: the next confirm retries the week only.
  const [monthRevoked, setMonthRevoked] = useState(false);
  const revoke = useRevokeSignOff();
  const pair = useRevokeWeekAndMonth();
  const pending = revoke.isPending || pair.isPending;
  const pairsMonth = month !== null && !monthRevoked;
  const copy = revokeConfirm({ pairsMonth, monthRevoked });
  const name = `Revoke sign-off for ${periodLabel(row)}`;
  const onClick = () => setOpen(true);

  function onOpenChange(next: boolean) {
    setOpen(next);
    if (!next) setMonthRevoked(false);
  }

  async function onConfirm() {
    if (month && pairsMonth) {
      // The hook toasts and refreshes; a partial result keeps the dialog open on the week.
      const result = await pair.mutateAsync({ monthId: month.id, weekId: row.id });
      if (result.month === "revoked" && result.week === "failed") {
        setMonthRevoked(true);
        return;
      }
      onOpenChange(false);
      return;
    }
    // The hook toasts either outcome; the dialog closes once the call is answered.
    await revoke.mutateAsync({ signOffId: row.id }).then(
      () => undefined,
      () => undefined,
    );
    onOpenChange(false);
  }

  return (
    <>
      {look === "header" ? (
        <Button
          variant="outline"
          className={TOUCH}
          aria-label={name}
          disabled={pending}
          onClick={onClick}
        >
          <Undo2 aria-hidden="true" />
          Revoke sign-off
        </Button>
      ) : (
        <Button
          type="button"
          variant="outline"
          className={TOUCH}
          aria-label={name}
          disabled={pending}
          onClick={onClick}
        >
          <Undo2 className="size-4" aria-hidden="true" />
          Revoke
        </Button>
      )}
      <ConfirmDialog
        open={open}
        onOpenChange={onOpenChange}
        title={`Revoke sign-off for ${periodLabel(row)}?`}
        description={copy.description}
        confirmLabel={copy.confirmLabel}
        variant="destructive"
        loading={pending}
        onConfirm={onConfirm}
      />
    </>
  );
}
